/**
 * Consent and tracking script management.
 *
 * Three independent consent signals:
 *
 *   vh_recording  — Opt-in for Smartlook session recording (CIPA consent).
 *                   Default: null (not accepted). Requires affirmative Accept
 *                   via the on-page notice. GPC → treated as null.
 *
 *   vh_sharing    — CCPA opt-out of Google Ads remarketing / behavioral
 *                   advertising ("sharing"). Default: "allowed". Declining
 *                   disables ad-personalization signals on the Google tag.
 *                   GPC → treated as "declined".
 *
 *   vh_analytics  — Opt-out of the Google Ads tag loading at all.
 *                   Default: "allowed". Declining prevents the gtag script
 *                   from injecting on subsequent page loads.
 *                   GPC → treated as "declined".
 *
 * Scripts NOT managed here (always load):
 *   • Intercom   — customer support service provider
 *   • Sender.net — newsletter signup, not behavioral advertising
 */

export const RECORDING_COOKIE  = "vh_recording";
export const SHARING_COOKIE    = "vh_sharing";
export const ANALYTICS_COOKIE  = "vh_analytics";
const TTL_DAYS = 365;

// ─── Cookie helpers ───────────────────────────────────────────────────────────

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, days: number) {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

function read(key: string): string | null {
  return readCookie(key) ?? (
    typeof localStorage !== "undefined" ? localStorage.getItem(key) : null
  );
}

function persist(key: string, value: string) {
  writeCookie(key, value, TTL_DAYS);
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(key, value);
  }
}

// ─── GPC ─────────────────────────────────────────────────────────────────────

export function isGpcActive(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

// ─── Recording consent (opt-in — Smartlook) ──────────────────────────────────

/** Returns "accepted" only if the user has explicitly consented and GPC is off. */
export function getRecordingConsent(): "accepted" | null {
  if (isGpcActive()) return null;
  return read(RECORDING_COOKIE) === "accepted" ? "accepted" : null;
}

export function setRecordingConsent(state: "accepted" | "declined"): void {
  persist(RECORDING_COOKIE, state);
}

// ─── Sharing consent (opt-out — CCPA "sharing") ──────────────────────────────

/** Returns "declined" if GPC is active or the user has explicitly opted out. */
export function getSharingConsent(): "allowed" | "declined" {
  if (isGpcActive()) return "declined";
  return read(SHARING_COOKIE) === "declined" ? "declined" : "allowed";
}

export function setSharingConsent(state: "allowed" | "declined"): void {
  persist(SHARING_COOKIE, state);
}

// ─── Analytics consent (opt-out — Google Ads tag) ────────────────────────────

/** Returns "declined" if GPC is active or the user has explicitly opted out. */
export function getAnalyticsConsent(): "allowed" | "declined" {
  if (isGpcActive()) return "declined";
  return read(ANALYTICS_COOKIE) === "declined" ? "declined" : "allowed";
}

export function setAnalyticsConsent(state: "allowed" | "declined"): void {
  persist(ANALYTICS_COOKIE, state);
}

// ─── Script loaders ───────────────────────────────────────────────────────────

/**
 * Fires a Google Ads conversion event.
 * The gtag script is loaded by gatsby-plugin-google-gtag — call this from
 * React onClick handlers wherever a conversion should be recorded.
 */
export function reportConversion(): void {
  if (typeof window === "undefined") return;
  window.gtag?.("event", "conversion", {
    send_to: "AW-17684009574/PnNmCOG3iLUbEOaksvBB",
    value: 1.0,
    currency: "USD",
  });
}

/**
 * Updates Google Consent Mode signals to reflect the current stored preferences.
 * Call this after the user saves choices on /privacy-choices.
 */
export function updateGtagConsent(): void {
  if (typeof window === "undefined" || !window.gtag) return;
  const analyticsGranted = getAnalyticsConsent() === "allowed" ? "granted" : "denied";
  const sharingGranted   = getSharingConsent()   === "allowed" ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage:        analyticsGranted,
    ad_user_data:      analyticsGranted,
    ad_personalization: sharingGranted,
    analytics_storage: analyticsGranted,
  });
}

/**
 * Loads Smartlook session recording.
 * Safe to call multiple times — guards against double-injection.
 * Only call after confirming getRecordingConsent() === "accepted".
 */
export function loadSmartlook(): void {
  if (typeof window === "undefined") return;
  if (window.smartlook) return;
  if (getRecordingConsent() !== "accepted") return;

  (function (d: Document) {
    const o = (window.smartlook = function (...args: unknown[]) {
      (o as unknown as { api: unknown[] }).api.push(args);
    }) as unknown as { api: unknown[] };
    o.api = [];
    const h = d.getElementsByTagName("head")[0];
    const c = d.createElement("script");
    c.async = true;
    c.type = "text/javascript";
    c.charset = "utf-8";
    c.src = "https://web-sdk.smartlook.com/recorder.js";
    h.appendChild(c);
  })(document);
  (window.smartlook as Function)("init", "9d5e7d8c97cb3ae1dd30ddc67aedf75ed506adec", {
    region: "eu",
  });
}
